<template>
    <landlords-find
        :params="{
            landlord: $route.params.landlord
        }"
        :include="[
            'account(include:business(include:address,proof_of_entity),persons(include:address,identity_card,proof_of_address))',
            'bank_account'
        ]"
    >
        <div
            slot-scope="{
                data: landlord,
                error: find_error,
                has_loaded,
                find
            }"
        >
            <template v-if="!has_loaded || find_error">
                <base-loading
                    :error="find_error"
                    :retry="find"
                    class="py-6"
                />

                <error-modal
                    v-if="find_error"
                    :error="find_error"
                />
            </template>

            <accounts-verification-update
                v-else
                :account="landlord.account.uuid"
                @success="onLandlordsVerificationUpdate"
            >
                <base-form
                    slot-scope="{
                        sid,
                        error,
                        is_loading: is_saving,
                        update
                    }"
                    @submit="update"
                >
                    <div class="px-4 py-6 border-b sm:px-6">
                        <validator-alert :sid="sid" />

                        <base-alert level="warning">
                            <template slot="heading">Double-check</template>
                            <template slot="content">
                                Please take a moment to carefully double-check your account details. After verification is
                                complete, these details are considered fact, and thus, it is not possible to change them
                                (excluding your bank account).
                            </template>
                        </base-alert>

                        <form-section>
                            <template slot="title">
                                <router-link
                                    :to="{ name: $router.fix('dashboard.*.verification.account.edit') }"
                                    class="no-underline text-blue"
                                >
                                    Account
                                </router-link>
                            </template>
                            <template slot="description">
                                Check that these are <strong>your details</strong>, they’re
                                <strong>legally correct</strong> (i.e. match your ID) and that
                                <strong>you are authorised</strong> to submit.
                            </template>

                            <with-variables
                                :variables="{
                                    person: _.find(landlord.account.persons.data, {
                                        is_account_opener: true
                                    })
                                }"
                            >
                                <grid-layout slot-scope="{ person }">
                                    <grid-layout-item class="sm:w-1/2">
                                        <div class="text-xs text-label">Name</div>
                                        <div class="mb-3 text-sm">{{ person.first_name }} {{ person.last_name }}</div>

                                        <div class="text-xs text-label">Date of Birth</div>
                                        <div class="mb-3 text-sm">{{ person.date_of_birth | moment('DD/MM/YYYY') }}</div>

                                        <div class="text-xs text-label">Email</div>
                                        <div class="mb-3 text-sm">{{ person.email }}</div>

                                        <div class="text-xs text-label">Telephone</div>
                                        <div class="mb-3 text-sm">+{{ person.telephone.number }}</div>
                                    </grid-layout-item>

                                    <grid-layout-item class="mb-3 sm:w-1/2">
                                        <div class="text-xs text-label">Address</div>
                                        <div class="text-sm">
                                            <base-address :address="person.address" />
                                        </div>
                                    </grid-layout-item>

                                    <grid-layout-item class="mb-3">
                                        <div class="text-xs text-label">Proof of Identity</div>
                                        <div class="text-sm">
                                            <div class="inline-block w-24 h-12">
                                                <file-card
                                                    :file="person.identity_card.front"
                                                    class="h-full"
                                                />
                                            </div>

                                            <div class="inline-block w-24 h-12 ml-3">
                                                <file-card
                                                    :file="person.identity_card.back"
                                                    class="h-full"
                                                />
                                            </div>
                                        </div>
                                    </grid-layout-item>

                                    <grid-layout-item class="mb-3">
                                        <div class="text-xs text-label">Proof of Address</div>
                                        <div class="text-sm">
                                            <div class="inline-block w-24 h-12">
                                                <file-card
                                                    :file="person.proof_of_address.front"
                                                    class="h-full"
                                                />
                                            </div>

                                            <div class="inline-block w-24 h-12 ml-3">
                                                <file-card
                                                    :file="person.proof_of_address.back"
                                                    class="h-full"
                                                />
                                            </div>
                                        </div>
                                    </grid-layout-item>
                                </grid-layout>
                            </with-variables>
                        </form-section>
                    </div>

                    <with-variables
                        :variables="{
                            account: landlord.account,
                            business: landlord.account.business,
                            persons: landlord.account.persons.data
                        }"
                    >
                        <div
                            slot-scope="{ account, business, persons }"
                            v-if="account.type === account.consts.type.business"
                            class="px-4 py-6 border-b sm:px-6"
                        >
                            <form-section>
                                <template slot="title">
                                    <router-link
                                        :to="{ name: $router.fix('dashboard.*.verification.business.edit') }"
                                        class="no-underline text-blue"
                                    >
                                        Business
                                    </router-link>
                                </template>
                                <template slot="description">
                                    Check that these business details are correct, and that you’ve
                                    added the details for any persons with more than a
                                    <strong>25% stake</strong> in the business.
                                </template>

                                <grid-layout>
                                    <grid-layout-item class="sm:w-1/2">
                                        <div class="text-xs text-label">Name</div>
                                        <div class="mb-3 text-sm">{{ business.name }}</div>

                                        <div class="text-xs text-label">CRN</div>
                                        <div class="mb-3 text-sm">{{ business.tax_id }}</div>

                                        <div class="text-xs text-label">VAT No.</div>
                                        <div class="mb-3 text-sm">
                                            <template v-if="business.vat_id">
                                                {{ business.vat_id }}
                                            </template>
                                            <base-na v-else />
                                        </div>
                                    </grid-layout-item>
                                    <grid-layout-item class="mb-3 sm:w-1/2">
                                        <div class="text-xs text-label">Address</div>
                                        <div class="text-sm">
                                            <base-address :address="business.address" />
                                        </div>
                                    </grid-layout-item>
                                    <grid-layout-item>
                                        <div class="text-xs text-label">Proof of Entity</div>
                                        <div class="text-sm">
                                            <div class="inline-block w-24 h-12">
                                                <file-card
                                                    :file="business.proof_of_entity.front"
                                                    class="h-full"
                                                />
                                            </div>
                                        </div>
                                    </grid-layout-item>
                                </grid-layout>

                                <div
                                    v-for="(person, index) in persons"
                                    :key="person.uuid"
                                    class="relative mt-3 mb-6 border rounded"
                                >
                                    <header class="flex items-center px-4 py-3 text-xs border-b text-label bg-grey-lightest">
                                        {{ index + 1 | ordinal }} Person

                                        <template v-if="person.is_account_opener">
                                            (You)
                                        </template>
                                    </header>

                                    <div class="px-4 pt-3">
                                        <grid-layout>
                                            <grid-layout-item :class="{ 'sm:w-1/2': !person.is_account_opener }">
                                                <template v-if="!person.is_account_opener">
                                                    <div class="text-xs text-label">Name</div>
                                                    <div class="mb-3 text-sm">{{ person.first_name }} {{ person.last_name }}</div>

                                                    <div class="text-xs text-label">Date of Birth</div>
                                                    <div class="mb-3 text-sm">{{ person.date_of_birth | moment('DD/MM/YYYY') }}</div>

                                                    <div class="text-xs text-label">Email</div>
                                                    <div class="mb-3 text-sm">{{ person.email }}</div>

                                                    <div class="text-xs text-label">Telephone</div>
                                                    <div class="mb-3 text-sm">+{{ person.telephone.number }}</div>
                                                </template>
                                            </grid-layout-item>

                                            <template v-if="!person.is_account_opener">
                                                <grid-layout-item class="mb-3 sm:w-1/2">
                                                    <div class="text-xs text-label">Address</div>
                                                    <div class="text-sm">
                                                        <base-address :address="person.address" />
                                                    </div>
                                                </grid-layout-item>
                                            </template>

                                            <grid-layout-item class="mb-3">
                                                <div class="text-xs text-label">Job Title</div>
                                                <div class="mb-3 text-sm">{{ person.job_title }}</div>

                                                <div class="text-xs text-label">Roles</div>
                                                <div class="mb-3 text-sm">
                                                    <template v-if="person.is_account_opener">
                                                        Account Opener (You).
                                                    </template>

                                                    <template v-if="person.is_director">
                                                        Director.
                                                    </template>

                                                    <template v-if="person.is_executive">
                                                        Execute.
                                                    </template>

                                                    <template v-if="person.is_owner">
                                                        Owner ({{ person.percent_ownership }}%).
                                                    </template>
                                                </div>
                                            </grid-layout-item>

                                            <template v-if="!person.is_account_opener">
                                                <grid-layout-item class="mb-3">
                                                    <div class="text-xs text-label">ID</div>
                                                    <div class="text-sm">
                                                        <div class="inline-block w-24 h-12">
                                                            <file-card
                                                                :file="person.identity_card.front"
                                                                class="h-full"
                                                            />
                                                        </div>

                                                        <div class="inline-block w-24 h-12 ml-3">
                                                            <file-card
                                                                :file="person.identity_card.back"
                                                                class="h-full"
                                                            />
                                                        </div>
                                                    </div>
                                                </grid-layout-item>

                                                <grid-layout-item class="mb-3">
                                                    <div class="text-xs text-label">Proof of Address</div>
                                                    <div class="text-sm">
                                                        <div class="inline-block w-24 h-12">
                                                            <file-card
                                                                :file="person.proof_of_address.front"
                                                                class="h-full"
                                                            />
                                                        </div>

                                                        <div class="inline-block w-24 h-12 ml-3">
                                                            <file-card
                                                                :file="person.proof_of_address.back"
                                                                class="h-full"
                                                            />
                                                        </div>
                                                    </div>
                                                </grid-layout-item>
                                            </template>
                                        </grid-layout>
                                    </div>
                                </div>
                            </form-section>
                        </div>
                    </with-variables>

                    <div class="px-4 py-6 border-b sm:px-6">
                        <form-section>
                            <template slot="title">
                                <router-link
                                    :to="{ name: $router.fix('dashboard.*.verification.bank.edit') }"
                                    class="no-underline text-blue"
                                >
                                    Bank
                                </router-link>
                            </template>
                            <template slot="description">
                                Check that this is the bank account that you want payments depositing into.
                            </template>

                            <grid-layout>
                                <with-variables :variables="{ landlord }">
                                    <grid-layout-item
                                        slot-scope="{ landlord: { bank_account: { last_four } } }"
                                        class="mb-3 sm:w-1/2"
                                    >
                                        <div class="text-xs text-label">Account</div>
                                        <div class="text-sm">
                                            <template v-if="last_four">
                                                **** {{ last_four }}
                                            </template>

                                            <router-link
                                                v-else
                                                :to="{ name: $router.fix('dashboard.*.verification.bank.edit') }"
                                                class="no-underline text-red"
                                            >
                                                No bank account
                                            </router-link>
                                        </div>
                                    </grid-layout-item>
                                </with-variables>
                            </grid-layout>
                        </form-section>
                    </div>

                    <div class="px-4 py-6 border-b sm:px-6">
                        <label class="flex items-center cursor-pointer md:pl-1/3">
                            <input-checkbox
                                v-model="has_confirmed"
                                class="mr-3"
                            />

                            <div class="leading-tight">I can confirm that this information is correct</div>
                        </label>
                    </div>

                    <div class="px-4 py-3 text-right bg-grey-bg sm:px-6">
                        <base-button
                            type="submit"
                            :is-loading="is_saving"
                            :disabled="!has_confirmed"
                            class="w-full btn-blue md:w-auto"
                        >
                            Submit for Verification
                        </base-button>
                    </div>

                    <error-modal
                        v-if="error && $error.is(error, { exclude: [400, 422] })"
                        :error="error"
                    />

                    <modal-success :is-open="show_submitted_modal">
                        <template slot="heading">Verification Submitted!</template>

                        <p>Thanks for submitting your details. Once we’ve verified your account, we’ll be in touch.</p>

                        <div class="flex mt-3">
                            <base-svg
                                name="icon-important-circle"
                                class="w-6 h-6 mr-2"
                            />

                            <span class="text-sm leading-tight text-left text-grey-darker mt-3px">
                                It usually takes <strong>few minutes</strong> to verify, but it can take <strong>few business days</strong>.
                                If it’s taken longer than 7 days, <span class="text-blue">get in touch</span>.
                            </span>
                        </div>

                        <template slot="footer">
                            <div class="text-right">
                                <router-link
                                    :to="{
                                        name: $router.fix('dashboard.*.overview.index'),
                                    }"
                                    class="text-center btn btn-green"
                                >
                                    Go to Overview
                                </router-link>
                            </div>
                        </template>
                    </modal-success>
                </base-form>
            </accounts-verification-update>
        </div>
    </landlords-find>
</template>

<script>
    import AccountsVerificationUpdate from '../../../../../components/accounts-verification-update'
    import FileCard from '../../../../../components/file-card'
    import LandlordsFind from '../../../../../components/landlords-find'

    export default {
        components: {
            AccountsVerificationUpdate,
            FileCard,
            LandlordsFind
        },
        data () {
            return {
                has_confirmed: false,
                show_submitted_modal: false
            }
        },
        methods: {
            onLandlordsVerificationUpdate () {
                this.$track(
                    'dashboard.landlords.verification.confirm.edit',
                    'form-submission',
                    'A landlord has completed step 4 (confirm) of verifying their account'
                )

                setTimeout(() => {
                    this.show_submitted_modal = true
                }, 0)
            }
        }
    }
</script>
